import { addressBlock } from '../components/address';
import { SOCIAL_PROFILES, configuredNetworks } from '../components/social';

import { tlink } from '../components/cta';
import { icon } from '../components/icons';
import { eyebrow, mesh } from '../components/primitives';
import { ctaBand, foundationTiles, howWeWork, northAmerica, sigBand } from '../components/sections';
import { SITE_CONFIG } from '../config/site-config';
import { EXTERNAL_LINK_ATTRS, NEW_TAB_NOTE, mailto, routeHref, salesMailto } from '../lib/links';
import { BIGGER_STORY, FOUNDER_QUOTE, INCEPTION_NOTE } from './shared-content';

/* ============================ COMPANY (Our Thesis) ============================ */
export function pageCompany(): string {
  const paths = [
    'an AI model',
    'an AI agent',
    'a business rule',
    'an API',
    'an existing system',
    'an automated workflow',
    'or the appropriate professional',
  ];
  return `
  <section class="hero compact" aria-labelledby="h1">
    ${mesh()}
    <div class="wrap rv">
      ${eyebrow('Our thesis')}
      <h1 id="h1">Built from inside <span class="grad">the problem.</span></h1>
      <p class="lede" style="margin-top:24px;max-width:760px">ReTax360 didn't begin as an idea for another software company. It began inside property tax operations. We saw experienced professionals spending too much of their time moving information between emails, documents, spreadsheets, systems and people. The expertise wasn't the problem. The infrastructure around the expertise was. ReTax360 was built to change that.</p>
    </div>
  </section>

  <section class="s" aria-labelledby="origin-h">
    <div class="wrap story">
      <div class="rv">${eyebrow('Origin')}<h2 id="origin-h" style="margin-top:16px">We built what we couldn't find.</h2></div>
      <div class="body rv" style="--d:80ms">
        <p>Property tax is complex by nature. Every client is different. Every jurisdiction has its own requirements. Every office develops its own methodology. Behind every appeal are documents, deadlines, communications, data, approvals and decisions that have to move between multiple people and systems.</p>
        <p>We experienced those challenges ourselves. Existing software could store information, but it didn't fully address how the work actually moved through the organization. So we started building our own system.</p>
        <p>What began as a way to improve property tax operations evolved into a larger idea: What if software could understand how an organization works—and intelligently coordinate the work around its people? That idea became ReTax360.</p>
      </div>
    </div>
  </section>

  ${sigBand("We didn't set out to replace property tax professionals. We set out to give them their day back.", { dark: true, founder: true, grad: 'give them their day back.' })}

  <section class="s" aria-labelledby="ct-h">
    <div class="wrap">
      <div class="grid2" style="gap:56px;align-items:start">
        <div class="rv">${eyebrow('Company thesis')}<h2 id="ct-h" style="margin:16px 0">Every firm works a little differently. The foundation doesn't.</h2>
          <p class="lede">Every property tax organization operates on the same foundational information: Clients. Communication. Data. Documents. ReTax360 brings those foundations together into one governed environment. Above that foundation sits an intelligence layer capable of determining where work should go next.</p></div>
        <div class="card rv"><div class="colh">That could be</div><ul class="checks" style="margin-top:0">${paths.map((p) => `<li>${icon('split')}<span>${p}</span></li>`).join('')}</ul>
          <p style="margin-top:18px">Not every task needs AI. Not every AI task needs the same model. And not every decision should be automated. The role of orchestration is to determine the appropriate path while maintaining governance, visibility and human control.</p></div>
      </div>
      ${INCEPTION_NOTE}
      <div style="margin-top:40px">${foundationTiles()}</div>
    </div>
  </section>

  <section class="s alt" aria-label="Founder">
    <div class="wrap">${FOUNDER_QUOTE}</div>
  </section>

  ${howWeWork({ full: true })}
  ${northAmerica()}

  <section class="s tight-top" aria-labelledby="cstory-h">
    <div class="wrap">${BIGGER_STORY('cstory-h')}</div>
  </section>

  ${
    configuredNetworks().length
      ? `
  <section class="s tight-top" aria-labelledby="fr-h">
    <div class="wrap">
      <div class="follow minimal rv">
        <div class="follow-head">${eyebrow('Follow ReTax360')}<h2 id="fr-h" class="h-cap" style="margin-top:14px">Follow what we're building.</h2><p class="muted">ReTax360 is continuing to explore how AI, orchestration and human expertise can reshape complex professional workflows. Follow our thinking, product development and company updates.</p></div>
        <ul class="slinks">${configuredNetworks()
          .map(
            (k) =>
              `<li><a class="tlink" href="${SOCIAL_PROFILES[k].url()}" ${EXTERNAL_LINK_ATTRS}>${icon(k, 'si')}${SOCIAL_PROFILES[k].cta} ${icon('arrow')}${NEW_TAB_NOTE}</a></li>`,
          )
          .join('')}</ul>
      </div>
    </div>
  </section>`
      : ''
  }

  ${sigBand("Configure. Don't conform.", { dark: true, grad: "Don't conform." })}

  <section class="s" aria-labelledby="ct-careers-h">
    <div class="wrap grid2" style="gap:20px">
      <div class="card rv"><span class="ic-tile">${icon('code2')}</span><h2 id="ct-careers-h" class="h-card">Interested in building with us?</h2><p>We're hiring across engineering, AI, marketing and go-to-market: passionate people who work with the latest technology and are ready to make decisions. We build our company the same way we build our platform.</p><ul class="tagrow" style="margin-top:16px">${['Senior Engineers', 'Full-Stack', 'Frontend', 'Backend', 'AI Engineers', 'Marketing', 'GTM'].map((t) => `<li class="tag">${t}</li>`).join('')}</ul><div style="margin-top:14px">${tlink('See open roles', routeHref('/careers#open-roles'))}</div></div>
      <div class="card rv"><span class="ic-tile">${icon('mail')}</span><h2 class="h-card">Contact</h2>${addressBlock()}<p style="margin-top:12px">Enterprise Sales: <a class="ul" href="${salesMailto()}">${SITE_CONFIG.SALES_EMAIL}</a><br>General &amp; Careers: <a class="ul" href="${mailto(SITE_CONFIG.INFO_EMAIL)}">${SITE_CONFIG.INFO_EMAIL}</a></p>
        <p class="legal-links" style="margin-top:12px"><a class="ul" href="${routeHref('/privacy')}">Privacy Policy</a> · <a class="ul" href="${routeHref('/terms')}">Terms of Service</a></p></div>
    </div>
  </section>
  ${ctaBand()}`;
}
